import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import FullScreenSpinner from '../components/FullScreenSpinner';

// Gate for authenticated areas (dashboard, editor). While the session bootstraps
// we show a spinner; an unauthenticated user is sent to /login, remembering where
// they were headed so login can bounce them back.
export default function ProtectedRoute({ children }: { children: ReactNode }) {
  const { me, loading } = useAuth();
  const location = useLocation();

  if (loading) return <FullScreenSpinner />;
  if (!me) return <Navigate to="/login" replace state={{ from: location }} />;
  // A session can exist while the inbox is still unproven — the Google callback
  // creates one for an account Google declines to vouch for (AUTH.md §3 row 7).
  // Those users finish verification before reaching the app, and keep their
  // intended destination across it. (The email+password path never gets this far:
  // the server refuses to issue a session at all until the code is entered.)
  //
  // Gated on the SERVER's policy, not on email_verified alone. Enforcing this
  // unconditionally would make REQUIRE_EMAIL_VERIFICATION a switch that isn't:
  // turning it off would free the API while the client still stranded every
  // unverified user here — the exact opposite of what you need in the incident
  // that makes you reach for it.
  if (me.email_verification_required && !me.email_verified) {
    return (
      <Navigate to="/verify-email" replace state={{ from: location.pathname + location.search }} />
    );
  }
  return <>{children}</>;
}
